import { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Check, X, MoreHorizontal, Trash2, Eye, EyeOff } from "lucide-react";
import { ColDef, ICellRendererParams, CellValueChangedEvent } from "ag-grid-community";

import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { AGDataGrid } from "@/components/ui/ag-data-grid";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { settingsService, profileService, type ServiceContext, type Setting } from "@/services";
import SettingFormDialog, { type SettingRecord } from "./SettingFormDialog";
import type { SettingCreate } from "@prolarus/shared";

type SettingRow = Setting & {
  created_by_profile?: { full_name: string | null } | null;
  updated_by_profile?: { full_name: string | null } | null;
};

export default function SettingsManager() {
  const { user } = useAuth();
  const { invalidateCache } = useSettings();
  const queryClient = useQueryClient();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<SettingRecord | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<Record<string, unknown> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState<SettingRow | null>(null);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());

  // Fetch user profile to get company_id
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  const getServiceContext = useCallback((): ServiceContext => ({
    companyId: companyId!,
    userId: user?.id,
  }), [companyId, user?.id]);

  // Fetch visible settings for the grid
  const { data: settingsResult, isLoading, isFetching } = useQuery({
    queryKey: ["settings-visible", companyId],
    queryFn: async () => {
      if (!companyId) return { data: [], error: null };
      const result = await settingsService.listVisible(companyId);
      if (result.error) throw result.error;
      return result;
    },
    enabled: !!companyId,
    placeholderData: (previousData) => previousData,
  });

  // Inline value edit handler
  const handleCellValueChanged = useCallback(async (event: CellValueChangedEvent<SettingRow>) => {
    const { data: rowData, colDef, oldValue, newValue } = event;
    if (!rowData || !companyId || oldValue === newValue) return;
    if (colDef.field !== 'setting_value') return;

    try {
      const result = await settingsService.updateValue(rowData.id, newValue, oldValue, getServiceContext());
      if (result.error) throw result.error;
      if (rowData.requires_restart) {
        invalidateCache();
      }
    } catch (error) {
      queryClient.invalidateQueries({ queryKey: ["settings-visible", companyId] });
      toast.error("Failed to update setting value");
      console.error(error);
    }
  }, [companyId, queryClient, getServiceContext, invalidateCache]);

  // Format value for display
  const formatValue = useCallback((row: SettingRow) => {
    if (row.is_sensitive && !revealedIds.has(row.id)) {
      return '••••••••';
    }
    const val = row.setting_value;
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  }, [revealedIds]);

  // Column definitions
  const columnDefs = useMemo<ColDef<SettingRow>[]>(() => [
    {
      field: "category",
      rowGroup: true,
      hide: true,
    },
    {
      field: "name",
      headerName: "Setting Name",
      flex: 2,
      minWidth: 200,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<SettingRow>) => {
        if (!params.data) return null;
        return (
          <div>
            <div className="font-medium">{params.data.name}</div>
            {params.data.description && (
              <div className="text-xs text-muted-foreground truncate">{params.data.description}</div>
            )}
          </div>
        );
      },
    },
    {
      field: "setting_value",
      headerName: "Value",
      flex: 2,
      minWidth: 200,
      editable: (params) => !params.data?.is_system,
      cellRenderer: (params: ICellRendererParams<SettingRow>) => {
        if (!params.data) return null;
        const row = params.data;
        if (row.is_sensitive) {
          const isRevealed = revealedIds.has(row.id);
          return (
            <div className="flex items-center gap-2">
              <span className={isRevealed ? '' : 'font-mono'}>{formatValue(row)}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  setRevealedIds(prev => {
                    const next = new Set(prev);
                    if (next.has(row.id)) next.delete(row.id);
                    else next.add(row.id);
                    return next;
                  });
                }}
              >
                {isRevealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </Button>
            </div>
          );
        }
        return <span>{formatValue(row)}</span>;
      },
      valueFormatter: (params) => {
        if (!params.data) return '';
        return formatValue(params.data);
      },
    },
    {
      field: "seq_num",
      headerName: "Order",
      width: 80,
      sortable: true,
    },
    {
      field: "is_system",
      headerName: "System",
      width: 90,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<SettingRow>) =>
        params.value ? (
          <Check className="w-4 h-4 text-primary mx-auto" />
        ) : (
          <X className="w-4 h-4 text-muted-foreground mx-auto" />
        ),
      cellClass: "text-center",
      headerClass: "ag-header-center",
    },
    {
      colId: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams<SettingRow>) => {
        const row = params.data;
        if (!row || row.is_system || !row.is_deletable) return null;
        return (
          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => handleDeleteClick(row)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ], [revealedIds, formatValue]);

  // Row click handler
  const handleRowClick = useCallback((row: SettingRow) => {
    setSelectedSnapshot({
      setting_key: row.setting_key,
      setting_value: row.setting_value,
      default_value: row.default_value,
      value_type: row.value_type,
      allowed_values: row.allowed_values,
      category: row.category,
      name: row.name,
      description: row.description,
      seq_num: row.seq_num,
      is_system: row.is_system,
      is_sensitive: row.is_sensitive,
      is_deletable: row.is_deletable,
      is_hidden_from_tenant: row.is_hidden_from_tenant,
      requires_restart: row.requires_restart,
    });
    setSelectedSetting({ ...row });
    setFormDialogOpen(true);
  }, []);

  // CRUD operations
  const handleNewClick = () => {
    setSelectedSetting(null);
    setSelectedSnapshot(null);
    setFormDialogOpen(true);
  };

  function handleDeleteClick(record: SettingRow) {
    setSettingToDelete(record);
    setDeleteDialogOpen(true);
  }

  const handleConfirmDelete = async () => {
    if (!settingToDelete || !companyId) return;

    const snapshot = {
      setting_key: settingToDelete.setting_key,
      setting_value: settingToDelete.setting_value,
      name: settingToDelete.name,
      category: settingToDelete.category,
    };

    try {
      const result = await settingsService.delete(settingToDelete.id, snapshot, getServiceContext());
      if (result.error) throw result.error;

      queryClient.invalidateQueries({ queryKey: ["settings-visible", companyId] });
      invalidateCache();
      toast.success("Setting deleted");
    } catch (error) {
      toast.error("Failed to delete setting");
      console.error(error);
    }

    setDeleteDialogOpen(false);
    setSettingToDelete(null);
  };

  const handleSave = async (data: SettingCreate) => {
    if (!companyId) return;

    try {
      if (selectedSetting) {
        const result = await settingsService.update(selectedSetting.id, data, selectedSnapshot || {}, getServiceContext());
        if (result.error) throw result.error;
        toast.success("Setting updated");
        if (data.requires_restart) {
          invalidateCache();
        }
      } else {
        const result = await settingsService.create(data, getServiceContext());
        if (result.error) throw result.error;
        toast.success("Setting created");
        invalidateCache();
      }

      queryClient.invalidateQueries({ queryKey: ["settings-visible", companyId] });
    } catch (error) {
      toast.error(selectedSetting ? "Failed to update setting" : "Failed to create setting");
      console.error(error);
    }
  };

  const handleDeleteFromDialog = (record: SettingRecord) => {
    const row = (settingsResult?.data || []).find(s => s.id === record.id);
    if (row) handleDeleteClick(row);
  };

  if (!companyId) {
    return <div className="text-center py-12 text-muted-foreground">Please set up your company profile first.</div>;
  }

  const toolbarActions = (
    <div className="flex items-center gap-2">
      <Button size="sm" onClick={handleNewClick}>
        <Plus className="w-4 h-4 mr-2" />
        Add Setting
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <AGDataGrid<SettingRow>
        rowData={settingsResult?.data || []}
        columnDefs={columnDefs}
        totalCount={settingsResult?.data?.length || 0}
        pageIndex={0}
        pageSize={999}
        isLoading={isLoading && !settingsResult}
        isFetching={isFetching}
        onCellValueChanged={handleCellValueChanged}
        onRowClicked={handleRowClick}
        toolbarActions={toolbarActions}
        groupDefaultExpanded={-1}
        height="calc(100vh - 400px)"
        minHeight="300px"
      />

      <SettingFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        setting={selectedSetting}
        onSave={handleSave}
        onDelete={handleDeleteFromDialog}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Setting</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{settingToDelete?.name}" ({settingToDelete?.setting_key})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
